import { useState, useEffect } from 'react';
import { supabase } from './supabase';
import { supabaseData } from './supabaseData';
import {
  Truck, Package, Plus, Edit2, Trash2, Save, X, Check, AlertTriangle,
  Loader, Search, Star, ClipboardList, ArrowRight, ChevronDown, ChevronUp,
  Warehouse, RefreshCw, CheckCircle, XCircle, Copy
} from 'lucide-react';
import { useModal } from './AppModal';
import { useTheme } from './ThemeContext';

export default function TruckLoadoutPage({ companyId }) {
  const { colors } = useTheme();
  const { showConfirm, showSuccess, showError } = useModal();

  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('compliance'); // 'compliance' or 'templates'

  // Templates state
  const [templates, setTemplates] = useState([]);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [templateForm, setTemplateForm] = useState({ name: '', description: '', is_default: false });
  const [templateItems, setTemplateItems] = useState([]); // items being edited in the modal

  // Inventory items for picker
  const [inventoryItems, setInventoryItems] = useState([]);
  const [itemSearch, setItemSearch] = useState('');

  // Compliance state
  const [locations, setLocations] = useState([]);
  const [stock, setStock] = useState({}); // { locationId: { itemId: quantity } }
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);
  const [expandedTrucks, setExpandedTrucks] = useState({});

  // Restock state
  const [showRestockModal, setShowRestockModal] = useState(false);
  const [restockLocation, setRestockLocation] = useState(null);
  const [restockItems, setRestockItems] = useState([]);
  const [warehouseLocation, setWarehouseLocation] = useState(null);

  const [isMobile, setIsMobile] = useState(window.innerWidth < 600);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 600);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (companyId) {
      loadAll();
    } else {
      setLoading(false);
    }
  }, [companyId]);

  const loadAll = async () => {
    try {
      setLoading(true);
      await Promise.all([loadTemplates(), loadLocations(), loadInventoryItems(), loadAllStock()]);
    } catch (error) {
      console.error('Error loading truck loadout data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadTemplates = async () => {
    const { data, error } = await supabaseData
      .from('truck_loadout_templates')
      .select(`
        *,
        truck_loadout_items(
          id,
          inventory_item_id,
          minimum_quantity,
          sort_order,
          inventory_items(id, name, category, sku)
        )
      `)
      .eq('company_id', companyId)
      .order('is_default', { ascending: false })
      .order('name');

    if (error) {
      console.error('Error loading templates:', error);
      return;
    }

    setTemplates(data || []);

    // Auto-select default template for compliance view
    if (!selectedTemplateId && data && data.length > 0) {
      const defaultTpl = data.find(t => t.is_default) || data[0];
      setSelectedTemplateId(defaultTpl.id);
    }
  };

  const loadLocations = async () => {
    const { data, error } = await supabaseData
      .from('locations')
      .select('id, name, location_type, is_active, is_default')
      .eq('company_id', companyId)
      .eq('is_active', true)
      .order('location_type')
      .order('name');

    if (error) {
      console.error('Error loading locations:', error);
      return;
    }

    setLocations(data || []);

    // Find warehouse for restock suggestions
    const wh = (data || []).find(l => l.location_type === 'warehouse');
    setWarehouseLocation(wh || null);
  };

  const loadInventoryItems = async () => {
    const { data, error } = await supabaseData
      .from('inventory_items')
      .select('id, name, category, sku')
      .eq('company_id', companyId)
      .eq('is_active', true)
      .order('name');

    if (error) {
      console.error('Error loading inventory items:', error);
      return;
    }

    setInventoryItems(data || []);
  };

  const loadAllStock = async () => {
    const { data, error } = await supabaseData
      .from('inventory_stock')
      .select('inventory_item_id, location_id, quantity_on_hand')
      .eq('company_id', companyId);

    if (error) {
      console.error('Error loading stock:', error);
      return;
    }

    // Build lookup: { locationId: { itemId: quantity } }
    const stockMap = {};
    (data || []).forEach(row => {
      if (!stockMap[row.location_id]) stockMap[row.location_id] = {};
      stockMap[row.location_id][row.inventory_item_id] = row.quantity_on_hand;
    });
    setStock(stockMap);
  };

  // ──────────────────── Template CRUD ────────────────────

  const openCreateTemplate = () => {
    setEditingTemplate(null);
    setTemplateForm({ name: '', description: '', is_default: false });
    setTemplateItems([]);
    setItemSearch('');
    setShowTemplateModal(true);
  };

  const openEditTemplate = (template) => {
    setEditingTemplate(template);
    setTemplateForm({
      name: template.name,
      description: template.description || '',
      is_default: template.is_default || false
    });
    setTemplateItems(
      (template.truck_loadout_items || [])
        .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
        .map(ti => ({
          id: ti.id,
          inventory_item_id: ti.inventory_item_id,
          minimum_quantity: ti.minimum_quantity,
          name: ti.inventory_items?.name || 'Unknown Item',
          category: ti.inventory_items?.category || '',
          sku: ti.inventory_items?.sku || ''
        }))
    );
    setItemSearch('');
    setShowTemplateModal(true);
  };

  const duplicateTemplate = async (template) => {
    try {
      const { data: newTpl, error } = await supabaseData
        .from('truck_loadout_templates')
        .insert({
          company_id: companyId,
          name: `${template.name} (Copy)`,
          description: template.description || '',
          is_default: false
        })
        .select()
        .single();

      if (error) throw error;

      // Copy items
      if (template.truck_loadout_items && template.truck_loadout_items.length > 0) {
        const itemInserts = template.truck_loadout_items.map((ti, idx) => ({
          template_id: newTpl.id,
          inventory_item_id: ti.inventory_item_id,
          minimum_quantity: ti.minimum_quantity,
          sort_order: ti.sort_order ?? idx
        }));

        const { error: itemError } = await supabaseData
          .from('truck_loadout_items')
          .insert(itemInserts);

        if (itemError) throw itemError;
      }

      showSuccess('Template duplicated successfully');
      await loadTemplates();
    } catch (error) {
      console.error('Error duplicating template:', error);
      showError('Failed to duplicate template');
    }
  };

  const saveTemplate = async () => {
    if (!templateForm.name.trim()) {
      showError('Template name is required');
      return;
    }

    try {
      setSavingTemplate(true);

      // If setting as default, unset others first
      if (templateForm.is_default) {
        await supabaseData
          .from('truck_loadout_templates')
          .update({ is_default: false })
          .eq('company_id', companyId);
      }

      let templateId;

      if (editingTemplate) {
        const { error } = await supabaseData
          .from('truck_loadout_templates')
          .update({
            name: templateForm.name.trim(),
            description: templateForm.description.trim(),
            is_default: templateForm.is_default
          })
          .eq('id', editingTemplate.id);

        if (error) throw error;
        templateId = editingTemplate.id;

        // Delete existing items, re-insert
        await supabaseData
          .from('truck_loadout_items')
          .delete()
          .eq('template_id', editingTemplate.id);
      } else {
        const { data: newTpl, error } = await supabaseData
          .from('truck_loadout_templates')
          .insert({
            company_id: companyId,
            name: templateForm.name.trim(),
            description: templateForm.description.trim(),
            is_default: templateForm.is_default
          })
          .select()
          .single();

        if (error) throw error;
        templateId = newTpl.id;
      }

      // Insert template items
      if (templateItems.length > 0) {
        const itemInserts = templateItems.map((ti, idx) => ({
          template_id: templateId,
          inventory_item_id: ti.inventory_item_id,
          minimum_quantity: ti.minimum_quantity,
          sort_order: idx
        }));

        const { error: itemError } = await supabaseData
          .from('truck_loadout_items')
          .insert(itemInserts);

        if (itemError) throw itemError;
      }

      showSuccess(editingTemplate ? 'Template updated' : 'Template created');
      setShowTemplateModal(false);
      await loadTemplates();
    } catch (error) {
      console.error('Error saving template:', error);
      showError('Failed to save template');
    } finally {
      setSavingTemplate(false);
    }
  };

  const deleteTemplate = async (template) => {
    const confirmed = await showConfirm(
      `Delete loadout template "${template.name}"? This cannot be undone.`,
      { confirmStyle: 'danger', confirmText: 'Delete' }
    );
    if (!confirmed) return;

    try {
      // Delete items first
      await supabaseData
        .from('truck_loadout_items')
        .delete()
        .eq('template_id', template.id);

      const { error } = await supabaseData
        .from('truck_loadout_templates')
        .delete()
        .eq('id', template.id);

      if (error) throw error;

      showSuccess('Template deleted');
      if (selectedTemplateId === template.id) {
        setSelectedTemplateId(null);
      }
      await loadTemplates();
    } catch (error) {
      console.error('Error deleting template:', error);
      showError('Failed to delete template');
    }
  };

  // ──────────────────── Template Items ────────────────────

  const addItemToTemplate = (item) => {
    if (templateItems.find(ti => ti.inventory_item_id === item.id)) return;
    setTemplateItems(prev => [...prev, {
      inventory_item_id: item.id,
      minimum_quantity: 1,
      name: item.name,
      category: item.category || '',
      sku: item.sku || ''
    }]);
  };

  const removeItemFromTemplate = (inventoryItemId) => {
    setTemplateItems(prev => prev.filter(ti => ti.inventory_item_id !== inventoryItemId));
  };

  const updateItemQuantity = (inventoryItemId, qty) => {
    const val = Math.max(1, parseInt(qty) || 1);
    setTemplateItems(prev => prev.map(ti =>
      ti.inventory_item_id === inventoryItemId ? { ...ti, minimum_quantity: val } : ti
    ));
  };

  // ──────────────────── Compliance helpers ────────────────────

  const getSelectedTemplate = () => templates.find(t => t.id === selectedTemplateId);

  const getTruckCompliance = (locationId) => {
    const template = getSelectedTemplate();
    if (!template || !template.truck_loadout_items) return { met: 0, total: 0, items: [] };

    const locationStock = stock[locationId] || {};
    const items = template.truck_loadout_items.map(ti => {
      const onHand = locationStock[ti.inventory_item_id] || 0;
      const needed = ti.minimum_quantity;
      return {
        inventory_item_id: ti.inventory_item_id,
        name: ti.inventory_items?.name || 'Unknown',
        category: ti.inventory_items?.category || '',
        minimum_quantity: needed,
        on_hand: onHand,
        met: onHand >= needed,
        deficit: Math.max(0, needed - onHand)
      };
    });

    const met = items.filter(i => i.met).length;
    return { met, total: items.length, items };
  };

  const truckLocations = locations.filter(l => l.location_type === 'truck');

  // ──────────────────── Restock ────────────────────

  const openRestock = (location) => {
    const compliance = getTruckCompliance(location.id);
    const missing = compliance.items.filter(i => !i.met);
    setRestockLocation(location);
    setRestockItems(missing);
    setShowRestockModal(true);
  };

  const toggleTruckExpanded = (locationId) => {
    setExpandedTrucks(prev => ({ ...prev, [locationId]: !prev[locationId] }));
  };

  // ──────────────────── Filtered inventory for picker ────────────────────

  const filteredInventoryItems = inventoryItems.filter(item => {
    if (!itemSearch.trim()) return true;
    const q = itemSearch.toLowerCase();
    return (
      (item.name || '').toLowerCase().includes(q) ||
      (item.category || '').toLowerCase().includes(q) ||
      (item.sku || '').toLowerCase().includes(q)
    );
  });

  // ──────────────────── RENDER ────────────────────

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 60 }}>
        <Loader size={24} style={{ animation: 'spin 1s linear infinite', color: colors.accent }} />
        <span style={{ marginLeft: 12, color: colors.textMuted }}>Loading truck loadouts...</span>
      </div>
    );
  }

  return (
    <div style={{ padding: isMobile ? 16 : 24, maxWidth: 1100, margin: '0 auto' }}>
      {/* Header */}
      <div style={{
        display: 'flex', alignItems: isMobile ? 'flex-start' : 'center',
        justifyContent: 'space-between',
        flexDirection: isMobile ? 'column' : 'row',
        gap: 12, marginBottom: 20
      }}>
        <div>
          <h2 style={{ margin: 0, color: colors.text, fontSize: isMobile ? 20 : 24, display: 'flex', alignItems: 'center', gap: 10 }}>
            <Truck size={isMobile ? 22 : 26} style={{ color: colors.accent }} />
            Truck Loadouts
          </h2>
          <p style={{ margin: '4px 0 0', color: colors.textMuted, fontSize: 14 }}>
            Define required items per truck and track compliance
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div style={{
        display: 'flex', gap: 0, marginBottom: 20,
        borderBottom: `2px solid ${colors.border}`,
      }}>
        {[
          { id: 'compliance', label: 'Truck Compliance', icon: CheckCircle },
          { id: 'templates', label: 'Loadout Templates', icon: ClipboardList }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            style={{
              padding: '10px 20px',
              background: 'none',
              border: 'none',
              borderBottom: activeTab === tab.id ? `2px solid ${colors.accent}` : '2px solid transparent',
              color: activeTab === tab.id ? colors.accent : colors.textMuted,
              fontWeight: activeTab === tab.id ? 600 : 400,
              fontSize: 14,
              cursor: 'pointer',
              display: 'flex', alignItems: 'center', gap: 6,
              marginBottom: -2
            }}
          >
            <tab.icon size={16} />
            {tab.label}
          </button>
        ))}
      </div>

      {/* ───── COMPLIANCE TAB ───── */}
      {activeTab === 'compliance' && (
        <div>
          {templates.length === 0 ? (
            <div style={{
              textAlign: 'center', padding: 40, color: colors.textMuted,
              background: colors.bgCard, borderRadius: 12, border: `1px solid ${colors.border}`
            }}>
              <ClipboardList size={40} style={{ marginBottom: 12, opacity: 0.5 }} />
              <p style={{ fontSize: 16, margin: '0 0 8px' }}>No loadout templates yet</p>
              <p style={{ fontSize: 14, margin: '0 0 16px' }}>Create a template to start tracking truck compliance.</p>
              <button
                onClick={() => { setActiveTab('templates'); openCreateTemplate(); }}
                style={{
                  padding: '10px 20px', background: colors.accent, color: '#fff',
                  border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: 14
                }}
              >
                <Plus size={16} style={{ marginRight: 6, verticalAlign: 'middle' }} />
                Create Template
              </button>
            </div>
          ) : (
            <>
              {/* Template selector */}
              <div style={{
                display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16,
                flexWrap: 'wrap'
              }}>
                <label style={{ color: colors.textMuted, fontSize: 14, fontWeight: 500 }}>Template:</label>
                <select
                  value={selectedTemplateId || ''}
                  onChange={e => setSelectedTemplateId(e.target.value)}
                  style={{
                    padding: '8px 12px', background: colors.bgInput, color: colors.text,
                    border: `1px solid ${colors.border}`, borderRadius: 8, fontSize: 14,
                    minWidth: 200
                  }}
                >
                  {templates.map(t => (
                    <option key={t.id} value={t.id}>
                      {t.name}{t.is_default ? ' (Default)' : ''}
                    </option>
                  ))}
                </select>
                <button
                  onClick={loadAll}
                  style={{
                    padding: '8px 12px', background: colors.bgInput, color: colors.textMuted,
                    border: `1px solid ${colors.border}`, borderRadius: 8, cursor: 'pointer',
                    display: 'flex', alignItems: 'center', gap: 6, fontSize: 13
                  }}
                >
                  <RefreshCw size={14} /> Refresh
                </button>
              </div>

              {truckLocations.length === 0 ? (
                <div style={{
                  textAlign: 'center', padding: 40, color: colors.textMuted,
                  background: colors.bgCard, borderRadius: 12, border: `1px solid ${colors.border}`
                }}>
                  <Truck size={40} style={{ marginBottom: 12, opacity: 0.5 }} />
                  <p style={{ fontSize: 16, margin: 0 }}>No truck locations found</p>
                  <p style={{ fontSize: 14, margin: '4px 0 0' }}>Add truck locations in the Locations page first.</p>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {truckLocations.map(loc => {
                    const compliance = getTruckCompliance(loc.id);
                    const pct = compliance.total > 0 ? Math.round((compliance.met / compliance.total) * 100) : 0;
                    const isExpanded = expandedTrucks[loc.id];
                    const allMet = compliance.met === compliance.total && compliance.total > 0;

                    return (
                      <div
                        key={loc.id}
                        style={{
                          background: colors.bgCard, borderRadius: 12,
                          border: `1px solid ${allMet ? colors.success + '44' : colors.border}`,
                          overflow: 'hidden'
                        }}
                      >
                        {/* Truck header row */}
                        <div
                          onClick={() => toggleTruckExpanded(loc.id)}
                          style={{
                            padding: isMobile ? '12px 14px' : '14px 20px',
                            display: 'flex', alignItems: 'center', gap: 12,
                            cursor: 'pointer', justifyContent: 'space-between'
                          }}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flex: 1, minWidth: 0 }}>
                            <Truck size={20} style={{ color: allMet ? colors.success : colors.warning, flexShrink: 0 }} />
                            <div style={{ minWidth: 0 }}>
                              <div style={{ fontWeight: 600, color: colors.text, fontSize: 15 }}>{loc.name}</div>
                              <div style={{ fontSize: 13, color: colors.textMuted }}>
                                {compliance.met}/{compliance.total} items stocked
                              </div>
                            </div>
                          </div>

                          {/* Progress bar */}
                          <div style={{
                            display: 'flex', alignItems: 'center', gap: 12,
                            flexShrink: 0
                          }}>
                            <div style={{
                              width: isMobile ? 80 : 140, height: 8, borderRadius: 4,
                              background: colors.bgInput, overflow: 'hidden'
                            }}>
                              <div style={{
                                width: `${pct}%`, height: '100%', borderRadius: 4,
                                background: pct === 100 ? colors.success : pct >= 60 ? colors.warning : colors.error,
                                transition: 'width 0.3s ease'
                              }} />
                            </div>
                            <span style={{
                              fontSize: 13, fontWeight: 600, minWidth: 36, textAlign: 'right',
                              color: pct === 100 ? colors.success : pct >= 60 ? colors.warning : colors.error
                            }}>
                              {pct}%
                            </span>
                            {!allMet && (
                              <button
                                onClick={(e) => { e.stopPropagation(); openRestock(loc); }}
                                style={{
                                  padding: '6px 12px', background: colors.accent, color: '#fff',
                                  border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 12,
                                  fontWeight: 600, whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: 4
                                }}
                              >
                                <Package size={13} /> Restock
                              </button>
                            )}
                            {isExpanded ? <ChevronUp size={18} style={{ color: colors.textMuted }} /> : <ChevronDown size={18} style={{ color: colors.textMuted }} />}
                          </div>
                        </div>

                        {/* Expanded item list */}
                        {isExpanded && compliance.items.length > 0 && (
                          <div style={{
                            borderTop: `1px solid ${colors.border}`,
                            padding: isMobile ? '8px 14px 14px' : '8px 20px 16px'
                          }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                              <thead>
                                <tr style={{ borderBottom: `1px solid ${colors.border}` }}>
                                  <th style={{ textAlign: 'left', padding: '8px 4px', fontSize: 12, color: colors.textMuted, fontWeight: 500 }}>Status</th>
                                  <th style={{ textAlign: 'left', padding: '8px 4px', fontSize: 12, color: colors.textMuted, fontWeight: 500 }}>Item</th>
                                  {!isMobile && <th style={{ textAlign: 'left', padding: '8px 4px', fontSize: 12, color: colors.textMuted, fontWeight: 500 }}>Category</th>}
                                  <th style={{ textAlign: 'center', padding: '8px 4px', fontSize: 12, color: colors.textMuted, fontWeight: 500 }}>Required</th>
                                  <th style={{ textAlign: 'center', padding: '8px 4px', fontSize: 12, color: colors.textMuted, fontWeight: 500 }}>On Hand</th>
                                  <th style={{ textAlign: 'center', padding: '8px 4px', fontSize: 12, color: colors.textMuted, fontWeight: 500 }}>Deficit</th>
                                </tr>
                              </thead>
                              <tbody>
                                {compliance.items.map(item => (
                                  <tr key={item.inventory_item_id} style={{ borderBottom: `1px solid ${colors.border}22` }}>
                                    <td style={{ padding: '8px 4px' }}>
                                      {item.met
                                        ? <CheckCircle size={18} style={{ color: colors.success }} />
                                        : <XCircle size={18} style={{ color: colors.error }} />
                                      }
                                    </td>
                                    <td style={{ padding: '8px 4px', color: colors.text, fontSize: 14 }}>{item.name}</td>
                                    {!isMobile && <td style={{ padding: '8px 4px', color: colors.textMuted, fontSize: 13 }}>{item.category}</td>}
                                    <td style={{ padding: '8px 4px', textAlign: 'center', color: colors.textMuted, fontSize: 14 }}>{item.minimum_quantity}</td>
                                    <td style={{
                                      padding: '8px 4px', textAlign: 'center', fontSize: 14, fontWeight: 600,
                                      color: item.met ? colors.success : colors.error
                                    }}>
                                      {item.on_hand}
                                    </td>
                                    <td style={{
                                      padding: '8px 4px', textAlign: 'center', fontSize: 14,
                                      color: item.deficit > 0 ? colors.error : colors.textMuted
                                    }}>
                                      {item.deficit > 0 ? `-${item.deficit}` : '--'}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* ───── TEMPLATES TAB ───── */}
      {activeTab === 'templates' && (
        <div>
          <div style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            marginBottom: 16
          }}>
            <span style={{ color: colors.textMuted, fontSize: 14 }}>
              {templates.length} template{templates.length !== 1 ? 's' : ''}
            </span>
            <button
              onClick={openCreateTemplate}
              style={{
                padding: '8px 16px', background: colors.accent, color: '#fff',
                border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: 14,
                display: 'flex', alignItems: 'center', gap: 6
              }}
            >
              <Plus size={16} /> New Template
            </button>
          </div>

          {templates.length === 0 ? (
            <div style={{
              textAlign: 'center', padding: 40, color: colors.textMuted,
              background: colors.bgCard, borderRadius: 12, border: `1px solid ${colors.border}`
            }}>
              <ClipboardList size={40} style={{ marginBottom: 12, opacity: 0.5 }} />
              <p style={{ fontSize: 16, margin: '0 0 4px' }}>No loadout templates</p>
              <p style={{ fontSize: 14, margin: 0 }}>Create your first template to define what should be on each truck.</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {templates.map(template => (
                <div
                  key={template.id}
                  style={{
                    background: colors.bgCard, borderRadius: 12,
                    border: `1px solid ${template.is_default ? colors.accent + '44' : colors.border}`,
                    padding: isMobile ? 14 : 20
                  }}
                >
                  <div style={{
                    display: 'flex', justifyContent: 'space-between',
                    alignItems: isMobile ? 'flex-start' : 'center',
                    flexDirection: isMobile ? 'column' : 'row', gap: 10
                  }}>
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ fontWeight: 600, color: colors.text, fontSize: 16 }}>{template.name}</span>
                        {template.is_default && (
                          <span style={{
                            fontSize: 11, padding: '2px 8px', borderRadius: 4,
                            background: colors.accent + '22', color: colors.accent, fontWeight: 600
                          }}>
                            DEFAULT
                          </span>
                        )}
                      </div>
                      {template.description && (
                        <p style={{ margin: '4px 0 0', color: colors.textMuted, fontSize: 13 }}>
                          {template.description}
                        </p>
                      )}
                      <p style={{ margin: '6px 0 0', color: colors.textMuted, fontSize: 13 }}>
                        <Package size={13} style={{ verticalAlign: 'middle', marginRight: 4 }} />
                        {(template.truck_loadout_items || []).length} items
                      </p>
                    </div>

                    <div style={{ display: 'flex', gap: 6 }}>
                      <button
                        onClick={() => duplicateTemplate(template)}
                        title="Duplicate"
                        style={{
                          padding: 8, background: colors.bgInput, color: colors.textMuted,
                          border: `1px solid ${colors.border}`, borderRadius: 6, cursor: 'pointer'
                        }}
                      >
                        <Copy size={15} />
                      </button>
                      <button
                        onClick={() => openEditTemplate(template)}
                        title="Edit"
                        style={{
                          padding: 8, background: colors.bgInput, color: colors.textMuted,
                          border: `1px solid ${colors.border}`, borderRadius: 6, cursor: 'pointer'
                        }}
                      >
                        <Edit2 size={15} />
                      </button>
                      <button
                        onClick={() => deleteTemplate(template)}
                        title="Delete"
                        style={{
                          padding: 8, background: colors.bgInput, color: colors.error,
                          border: `1px solid ${colors.border}`, borderRadius: 6, cursor: 'pointer'
                        }}
                      >
                        <Trash2 size={15} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ───── TEMPLATE MODAL ───── */}
      {showTemplateModal && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.6)', zIndex: 1000,
          display: 'flex', justifyContent: 'center', alignItems: 'flex-start',
          padding: isMobile ? '20px 8px' : '40px 20px',
          overflowY: 'auto'
        }}>
          <div style={{
            background: colors.bgCard, borderRadius: 16,
            border: `1px solid ${colors.border}`,
            width: '100%', maxWidth: 700,
            maxHeight: '90vh', overflowY: 'auto',
            padding: isMobile ? 16 : 24
          }}>
            {/* Modal header */}
            <div style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              marginBottom: 20
            }}>
              <h3 style={{ margin: 0, color: colors.text, fontSize: 18 }}>
                {editingTemplate ? 'Edit Template' : 'New Loadout Template'}
              </h3>
              <button
                onClick={() => setShowTemplateModal(false)}
                style={{
                  background: 'none', border: 'none', color: colors.textMuted,
                  cursor: 'pointer', padding: 4
                }}
              >
                <X size={20} />
              </button>
            </div>

            {/* Template name & description */}
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontSize: 13, color: colors.textMuted, marginBottom: 4, fontWeight: 500 }}>
                Template Name *
              </label>
              <input
                type="text"
                value={templateForm.name}
                onChange={e => setTemplateForm(prev => ({ ...prev, name: e.target.value }))}
                placeholder="e.g. Standard Truck, HVAC Truck"
                style={{
                  width: '100%', padding: '10px 12px', background: colors.bgInput, color: colors.text,
                  border: `1px solid ${colors.border}`, borderRadius: 8, fontSize: 14,
                  boxSizing: 'border-box'
                }}
              />
            </div>

            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontSize: 13, color: colors.textMuted, marginBottom: 4, fontWeight: 500 }}>
                Description
              </label>
              <input
                type="text"
                value={templateForm.description}
                onChange={e => setTemplateForm(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Optional description"
                style={{
                  width: '100%', padding: '10px 12px', background: colors.bgInput, color: colors.text,
                  border: `1px solid ${colors.border}`, borderRadius: 8, fontSize: 14,
                  boxSizing: 'border-box'
                }}
              />
            </div>

            <label style={{
              display: 'flex', alignItems: 'center', gap: 8, marginBottom: 20,
              cursor: 'pointer', fontSize: 14, color: colors.text
            }}>
              <input
                type="checkbox"
                checked={templateForm.is_default}
                onChange={e => setTemplateForm(prev => ({ ...prev, is_default: e.target.checked }))}
                style={{ accentColor: colors.accent }}
              />
              <Star size={15} style={{ color: colors.accent }} />
              Set as default template
            </label>

            {/* Divider */}
            <div style={{ borderBottom: `1px solid ${colors.border}`, marginBottom: 16 }} />

            {/* Current template items */}
            <h4 style={{ margin: '0 0 10px', color: colors.text, fontSize: 15 }}>
              Template Items ({templateItems.length})
            </h4>

            {templateItems.length > 0 && (
              <div style={{
                marginBottom: 16, display: 'flex', flexDirection: 'column', gap: 6
              }}>
                {templateItems.map(ti => (
                  <div
                    key={ti.inventory_item_id}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 10,
                      padding: '8px 12px', background: colors.bgInput, borderRadius: 8,
                      border: `1px solid ${colors.border}`
                    }}
                  >
                    <Package size={14} style={{ color: colors.textMuted, flexShrink: 0 }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 14, color: colors.text, fontWeight: 500 }}>{ti.name}</div>
                      {ti.category && <div style={{ fontSize: 12, color: colors.textMuted }}>{ti.category}</div>}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                      <label style={{ fontSize: 12, color: colors.textMuted }}>Min:</label>
                      <input
                        type="number"
                        min="1"
                        value={ti.minimum_quantity}
                        onChange={e => updateItemQuantity(ti.inventory_item_id, e.target.value)}
                        style={{
                          width: 60, padding: '4px 6px', background: colors.bgCard, color: colors.text,
                          border: `1px solid ${colors.border}`, borderRadius: 6, fontSize: 14,
                          textAlign: 'center'
                        }}
                      />
                    </div>
                    <button
                      onClick={() => removeItemFromTemplate(ti.inventory_item_id)}
                      style={{
                        background: 'none', border: 'none', color: colors.error,
                        cursor: 'pointer', padding: 4, flexShrink: 0
                      }}
                    >
                      <X size={16} />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Item picker */}
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontSize: 13, color: colors.textMuted, marginBottom: 4, fontWeight: 500 }}>
                Add Items from Inventory
              </label>
              <div style={{ position: 'relative', marginBottom: 8 }}>
                <Search size={16} style={{
                  position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)',
                  color: colors.textMuted
                }} />
                <input
                  type="text"
                  value={itemSearch}
                  onChange={e => setItemSearch(e.target.value)}
                  placeholder="Search inventory items..."
                  style={{
                    width: '100%', padding: '8px 12px 8px 32px', background: colors.bgInput, color: colors.text,
                    border: `1px solid ${colors.border}`, borderRadius: 8, fontSize: 14,
                    boxSizing: 'border-box'
                  }}
                />
              </div>

              <div style={{
                maxHeight: 180, overflowY: 'auto', border: `1px solid ${colors.border}`,
                borderRadius: 8, background: colors.bgInput
              }}>
                {filteredInventoryItems.length === 0 ? (
                  <div style={{ padding: 16, textAlign: 'center', color: colors.textMuted, fontSize: 13 }}>
                    {inventoryItems.length === 0 ? 'No inventory items found. Add items in Inventory first.' : 'No items match your search.'}
                  </div>
                ) : (
                  filteredInventoryItems.map(item => {
                    const alreadyAdded = templateItems.some(ti => ti.inventory_item_id === item.id);
                    return (
                      <div
                        key={item.id}
                        onClick={() => !alreadyAdded && addItemToTemplate(item)}
                        style={{
                          padding: '8px 12px',
                          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                          borderBottom: `1px solid ${colors.border}22`,
                          cursor: alreadyAdded ? 'default' : 'pointer',
                          opacity: alreadyAdded ? 0.5 : 1,
                          background: alreadyAdded ? 'transparent' : undefined
                        }}
                        onMouseEnter={e => { if (!alreadyAdded) e.currentTarget.style.background = colors.bgHover || colors.border + '33'; }}
                        onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                      >
                        <div>
                          <div style={{ fontSize: 14, color: colors.text }}>{item.name}</div>
                          <div style={{ fontSize: 12, color: colors.textMuted }}>
                            {[item.category, item.sku].filter(Boolean).join(' · ')}
                          </div>
                        </div>
                        {alreadyAdded ? (
                          <Check size={16} style={{ color: colors.success }} />
                        ) : (
                          <Plus size={16} style={{ color: colors.accent }} />
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            </div>

            {/* Modal actions */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, paddingTop: 8 }}>
              <button
                onClick={() => setShowTemplateModal(false)}
                style={{
                  padding: '10px 20px', background: colors.bgInput, color: colors.textMuted,
                  border: `1px solid ${colors.border}`, borderRadius: 8, cursor: 'pointer', fontSize: 14
                }}
              >
                Cancel
              </button>
              <button
                onClick={saveTemplate}
                disabled={savingTemplate}
                style={{
                  padding: '10px 20px', background: colors.accent, color: '#fff',
                  border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: 14,
                  opacity: savingTemplate ? 0.7 : 1,
                  display: 'flex', alignItems: 'center', gap: 6
                }}
              >
                {savingTemplate ? <Loader size={16} style={{ animation: 'spin 1s linear infinite' }} /> : <Save size={16} />}
                {editingTemplate ? 'Update Template' : 'Create Template'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ───── RESTOCK MODAL ───── */}
      {showRestockModal && restockLocation && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.6)', zIndex: 1000,
          display: 'flex', justifyContent: 'center', alignItems: 'flex-start',
          padding: isMobile ? '20px 8px' : '40px 20px',
          overflowY: 'auto'
        }}>
          <div style={{
            background: colors.bgCard, borderRadius: 16,
            border: `1px solid ${colors.border}`,
            width: '100%', maxWidth: 550,
            padding: isMobile ? 16 : 24
          }}>
            {/* Header */}
            <div style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              marginBottom: 20
            }}>
              <h3 style={{ margin: 0, color: colors.text, fontSize: 18, display: 'flex', alignItems: 'center', gap: 8 }}>
                <Package size={20} style={{ color: colors.accent }} />
                Restock: {restockLocation.name}
              </h3>
              <button
                onClick={() => setShowRestockModal(false)}
                style={{
                  background: 'none', border: 'none', color: colors.textMuted,
                  cursor: 'pointer', padding: 4
                }}
              >
                <X size={20} />
              </button>
            </div>

            {restockItems.length === 0 ? (
              <div style={{ padding: 20, textAlign: 'center', color: colors.textMuted }}>
                <CheckCircle size={32} style={{ color: colors.success, marginBottom: 8 }} />
                <p style={{ margin: 0, fontSize: 15 }}>All items are stocked!</p>
              </div>
            ) : (
              <>
                <p style={{ margin: '0 0 12px', color: colors.textMuted, fontSize: 14 }}>
                  {restockItems.length} item{restockItems.length !== 1 ? 's' : ''} below minimum:
                </p>

                <div style={{
                  display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 16
                }}>
                  {restockItems.map(item => (
                    <div
                      key={item.inventory_item_id}
                      style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        padding: '10px 14px', background: colors.bgInput, borderRadius: 8,
                        border: `1px solid ${colors.error}33`
                      }}
                    >
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 14, color: colors.text, fontWeight: 500 }}>{item.name}</div>
                        {item.category && <div style={{ fontSize: 12, color: colors.textMuted }}>{item.category}</div>}
                      </div>
                      <div style={{ textAlign: 'right', flexShrink: 0, marginLeft: 12 }}>
                        <div style={{ fontSize: 14, fontWeight: 600, color: colors.error }}>
                          Need {item.deficit}
                        </div>
                        <div style={{ fontSize: 12, color: colors.textMuted }}>
                          {item.on_hand} of {item.minimum_quantity}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Warehouse transfer suggestion */}
                {warehouseLocation && (
                  <div style={{
                    padding: 14, background: colors.accent + '11', borderRadius: 8,
                    border: `1px solid ${colors.accent}33`, marginBottom: 16
                  }}>
                    <div style={{
                      display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6,
                      color: colors.accent, fontWeight: 600, fontSize: 14
                    }}>
                      <Warehouse size={16} />
                      Transfer from {warehouseLocation.name}
                    </div>
                    <div style={{ fontSize: 13, color: colors.textMuted }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <span>{warehouseLocation.name}</span>
                        <ArrowRight size={14} />
                        <span>{restockLocation.name}</span>
                      </div>
                      <p style={{ margin: '6px 0 0', fontSize: 13 }}>
                        Use the Inventory Transfer feature to move these items from your warehouse to this truck.
                      </p>
                    </div>
                  </div>
                )}
              </>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end', paddingTop: 8 }}>
              <button
                onClick={() => setShowRestockModal(false)}
                style={{
                  padding: '10px 20px', background: colors.bgInput, color: colors.textMuted,
                  border: `1px solid ${colors.border}`, borderRadius: 8, cursor: 'pointer', fontSize: 14
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
